import { useCallback, useEffect, useState, useMemo } from 'react'
import { sortBy, partition } from 'lodash-es'

import { ISavedStateFilteredRoute } from '$/types'
import { useAppSelector } from 'app/hooks'

import { useGetQRASavedStatesQuery } from 'app/services/api/qra/savedstates'
import { useGetCurrentUserIdQuery } from 'app/services/api/users'
import { selectAllContinuousRoutes } from 'app/layout/Navbar/Filters/globalFilterSelectors'

import LoadingSpinner from 'common/components/LoadingSpinner'
import { SearchInput } from 'common/components/SearchInput'

import QRACompareStatesButton from 'features/RiskManagement/Components/QRACompareStatesButton'
import SavedStatesContainer from 'features/RiskManagement/Components/SavedStatesContainer'
import { ISavedState } from 'features/RiskManagement/Assessments'
import { TypeCode } from 'features/RiskManagement/Assessments/TypeCode'

interface ISavedStatesListsMemo {
  allPublishedList: ISavedState[]
  allUnpublishedList: ISavedState[]
}

const SavedStateList = () => {
  const allNetworkLines = useAppSelector(selectAllContinuousRoutes)
  const { currentData: userId } = useGetCurrentUserIdQuery()
  const { currentData: states, isFetching } = useGetQRASavedStatesQuery()

  const statesWithRouteNames = useMemo(() => {
    /*
      BE, for the time being, doesn't return the route names for each route id included at the moment
      the state was saved. Hence, this useMemo calculates each route name for each route id as
      a temporal solution until this new property takes part of the BE response
    */
    const getNameForRoute = (id: number) => {
      return allNetworkLines.find((line) => line.id === id)?.name || ''
    }

    const includeRouteNameToRoutes = (
      routes: ISavedStateFilteredRoute[]
    ): ISavedStateFilteredRoute[] =>
      routes.map(
        (route: ISavedStateFilteredRoute): ISavedStateFilteredRoute => {
          const routeName: string = getNameForRoute(route.continuousRouteId)

          return {
            ...route,
            routeName
          }
        }
      )

    const includeRouteNameToStates = (states: ISavedState[]): ISavedState[] =>
      states.map((state) => {
        const routesWithNames = includeRouteNameToRoutes(
          /* ?? [] is to avoid FE crash until Sharath merges his BE part
             here https://janatechnology.atlassian.net/browse/LHT-2271.
             After that can be chipped away
          */
          state.savedStateContinuousRoutes ?? []
        )

        return {
          ...state,
          savedStateContinuousRoutes: routesWithNames
        }
      })

    return includeRouteNameToStates(states ?? [])
  }, [allNetworkLines, states])

  const { allPublishedList, allUnpublishedList } = useMemo(() => {
    const baseSavedStatesLists: ISavedStatesListsMemo = {
      allPublishedList: [],
      allUnpublishedList: []
    }

    if (statesWithRouteNames) {
      const now = new Date().getTime()

      const dateSorter = (savedState: ISavedState) =>
        now - new Date(savedState.dateCreated).getTime()

      // sort saved states by date and filter by published criteria
      return sortBy(statesWithRouteNames, dateSorter).reduce(
        (accumulator: ISavedStatesListsMemo, savedState: ISavedState) => {
          // default condition push saved states to publish
          if (savedState.assessments.length == 0) {
            if (savedState.createdBy === userId) {
              accumulator.allUnpublishedList.push(savedState)

              return accumulator
            }

            return accumulator
          }

          const wisAssessments = savedState.assessments.filter(
            (assessment) => assessment.typeCode.toUpperCase() === TypeCode.WIS
          )

          const [publishedAssessments, unPublishedAssessments] = partition(
            wisAssessments,
            (assessment) => assessment.isPublished
          )

          // if unpublished WIS exists, update the assessments list
          if (unPublishedAssessments.length) {
            // copy state not to mutate redux objects
            const copiedState = { ...savedState }

            copiedState.assessments = unPublishedAssessments

            accumulator.allUnpublishedList.push(copiedState)
          }

          if (publishedAssessments.length) {
            const copiedState = { ...savedState }

            copiedState.assessments = publishedAssessments

            accumulator.allPublishedList.push(copiedState)
          }

          return accumulator
        },
        baseSavedStatesLists
      )
    }

    return baseSavedStatesLists
  }, [statesWithRouteNames, userId])

  const [publishedList, setPublishedList] =
    useState<ISavedState[]>(allPublishedList)

  const [unpublishedList, setUnpublishedList] =
    useState<ISavedState[]>(allUnpublishedList)

  const [searchText, setSearchText] = useState<string>('')

  const handleSearchInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setSearchText(event.target.value),
    []
  )

  // init lists
  useEffect(() => {
    setPublishedList(allPublishedList)
    setUnpublishedList(allUnpublishedList)
  }, [allPublishedList, allUnpublishedList])

  // debounce searching
  useEffect(() => {
    if (searchText.length) {
      const timeoutID = setTimeout(() => {
        const matchSearchText = (listItem: ISavedState) =>
          listItem.name.toLowerCase().includes(searchText.toLowerCase())

        setPublishedList(allPublishedList.filter(matchSearchText))
        setUnpublishedList(allUnpublishedList.filter(matchSearchText))
      }, 222)

      return () => {
        clearTimeout(timeoutID)
      }
    } else {
      setPublishedList(allPublishedList)
      setUnpublishedList(allUnpublishedList)
    }
  }, [allPublishedList, allUnpublishedList, searchText])

  return (
    <div className='flex flex-1 flex-col overflow-auto bg-gray-100'>
      <div className='flex px-6 pb-3 pt-14 xl:px-[6.125rem]'>
        <div className='flex h-12 flex-1 flex-col justify-around'>
          <h2 className='text-lighthouseBlue'>RISK MANAGEMENT</h2>
          <span className='mb-4 mt-2 text-3xl font-bold'>Saved States</span>
        </div>
      </div>
      <div className='flex px-6 py-3 xl:px-[6.125rem]'>
        <SearchInput
          className='w-80'
          placeholder='Search Saved States...'
          onChange={handleSearchInput}
        />
        <div className='ml-auto'>
          <QRACompareStatesButton
            stateList={
              states?.filter(
                (s) =>
                  allPublishedList.some(
                    (publishedState) => publishedState.name === s.name
                  ) ||
                  allUnpublishedList.some(
                    (unpublishedState) => unpublishedState.name === s.name
                  )
              ) ?? []
            }
          />
        </div>
      </div>
      <div className='overflow-auto xl:mr-[6.125rem]'>
        <LoadingSpinner isLoading={isFetching}>
          <SavedStatesContainer
            stateType='Unpublished'
            subheading='Only visible to you'
            baselineList={unpublishedList}
          />
          <SavedStatesContainer
            stateType='Network'
            baselineList={publishedList}
          />
        </LoadingSpinner>
      </div>
    </div>
  )
}

export default SavedStateList
